import type { IHostMessage } from '../protocol';

/**
 * Низкоуровневый транспорт оболочка ↔ iframe через postMessage (cross-origin).
 * Безопасность: исходящие шлём только на явный targetOrigin; входящие принимаем только
 * с разрешённого origin и только от окна нашего iframe.
 */
export interface IIframeTransportOptions {
  /** Окно iframe, куда слать сообщения (iframe.contentWindow). */
  getTarget: () => Window | null;
  /** Origin сервиса — целевой для postMessage. Пусто → исходящие не шлём. */
  targetOrigin: string;
  /** Допустимые origin входящих сообщений. Пусто → без ограничения (dev). */
  allowedOrigins: string[];
  /** Колбэк на валидное входящее сообщение (сырые данные из event.data). */
  onMessage: (raw: unknown) => void;
}

export interface IIframeTransport {
  /** Отправить сообщение оболочка → сервис (сериализуется в строку, как ждёт __nativeBridge). */
  post: (message: IHostMessage) => void;
  /** Отписаться от window message и освободить ресурсы. */
  destroy: () => void;
}

export function createIframeTransport(
  options: IIframeTransportOptions
): IIframeTransport {
  const { getTarget, targetOrigin, allowedOrigins, onMessage } = options;

  const handleMessage = (event: MessageEvent) => {
    if (allowedOrigins.length > 0 && !allowedOrigins.includes(event.origin)) {
      return;
    }
    const target = getTarget();
    // Если iframe уже смонтирован — принимаем только его сообщения.
    if (target && event.source !== target) return;
    onMessage(event.data);
  };

  window.addEventListener('message', handleMessage);

  return {
    post: (message) => {
      const target = getTarget();
      if (!target || !targetOrigin) return;
      target.postMessage(JSON.stringify(message), targetOrigin);
    },
    destroy: () => {
      window.removeEventListener('message', handleMessage);
    }
  };
}
